import type { Dispatch, JSX, MouseEvent, SetStateAction } from 'react'
import type { PopupPadding } from './Popup.h'

import { Children, useCallback, useEffect, useRef, useState } from 'react'
import { ErrorMessage } from 'atoms/ErrorMessage'
import { ImagePoster } from 'atoms/ImagePoster'
import { useScrolling } from 'utils/hooks'
import { COLOR, INDENTS } from 'vars/index'
import { CloseButtonPopup } from './components/CloseButtonPopup'
import {
  PopupAlignItems,
  PopupColumnGap,
  PopupDirection,
  PopupPaddings,
  PopupRowGap,
} from './Popup.h'
import {
  PopupFooterWrapperSC,
  PopupGradientOverlay,
  PopupHeaderSC,
  PopupOverlay,
  PopupScrollableBlockDefault,
  PopupScrollableBlockWithGradient,
  PopupWindow,
} from './Popup.styled'

export interface IPopupProps {
  children: React.ReactNode
  width?: string
  height?: number
  direction?: PopupDirection
  alignItems?: PopupAlignItems
  padding?: PopupPadding
  rowGap?: PopupRowGap
  columnGap?: PopupColumnGap
  onClose?: () => void
  hasCloseOnClickOverlay?: boolean
  rowGapDescription?: PopupRowGap
  isCloseBtnShown?: boolean
  buttons?: React.ReactNode[]
  errorMessage?: string
  transparent?: boolean
  setPopupHeaderHeight?: Dispatch<SetStateAction<number>>

  withGradient?: boolean
  headerBackground?: COLOR
  popupBackground?: COLOR
  posterUrl?: string
}

export const Popup = ({
  children,
  width = '33rem',
  height,
  direction = PopupDirection.Column,
  alignItems = PopupAlignItems.Stretch,
  padding = PopupPaddings.SIZE_40,
  rowGap = PopupRowGap.INDENT_60,
  columnGap = PopupColumnGap.INDENT_20,
  onClose,
  hasCloseOnClickOverlay = true,
  rowGapDescription = PopupRowGap.INDENT_60,
  isCloseBtnShown = true,
  errorMessage,
  setPopupHeaderHeight,

  withGradient = true,
  headerBackground = COLOR.SURFACE_DARK_4,
  popupBackground = COLOR.SURFACE_DARK_4,
  posterUrl,
}: IPopupProps): JSX.Element => {
  const popupHeaderRef = useRef<HTMLDivElement>(null)
  const popupBodyRef = useRef<HTMLDivElement>(null)
  const popupOverlayRef = useRef<HTMLDivElement>(null)

  const [isTopGradientShown, setIsTopGradientShown] = useState(false)
  const [isBottomGradientShown, setIsBottomGradientShown] = useState(false)
  const [isWithPoster, setIsWithPoster] = useState(false)

  const handleImageLoading = () => {
    setIsWithPoster(true)
  }

  useScrolling(popupOverlayRef)

  useEffect(() => {
    const popupBody = popupBodyRef.current

    if (popupBody) {
      setIsBottomGradientShown(popupBody.scrollHeight > popupBody.clientHeight)
    }
  }, [popupBodyRef])

  useEffect(() => {
    const popupHeader = popupHeaderRef.current
    if (setPopupHeaderHeight && popupHeader)
      setPopupHeaderHeight(popupHeader.clientHeight)
  }, [setPopupHeaderHeight, popupHeaderRef])

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const currentTarget = e.currentTarget
    const scrollTop = currentTarget.scrollTop

    setIsTopGradientShown(scrollTop > 1)

    const isScrolledToBottom =
      currentTarget.scrollHeight - scrollTop - currentTarget.clientHeight > 1
    setIsBottomGradientShown(isScrolledToBottom)
  }

  const components = Children.toArray(children)

  const handleClose = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation()
      if (onClose) onClose()
    },
    [onClose]
  )

  const handleEscButton = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onClose) onClose()
    },
    [onClose]
  )

  useEffect(() => {
    document.addEventListener('keydown', handleEscButton)
    return () => document.removeEventListener('keydown', handleEscButton)
  }, [handleEscButton])

  const popupWindowClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation()
  }

  const onClickOverlay = useCallback(
    (e: React.MouseEvent) => {
      if (!hasCloseOnClickOverlay) return
      return handleClose(e)
    },
    [hasCloseOnClickOverlay, handleClose]
  )

  return components && components.length ? (
    <PopupOverlay ref={popupOverlayRef} onClick={onClickOverlay}>
      <PopupWindow
        width={width}
        height={height}
        direction={direction}
        alignItems={alignItems}
        padding={padding}
        rowGap={rowGap}
        columnGap={columnGap}
        popupBackground={popupBackground}
        onClick={popupWindowClick}
      >
        <PopupHeaderSC
          ref={popupHeaderRef}
          background={headerBackground}
          withPosterUrl={isWithPoster}
        >
          {components[0]}
          {isCloseBtnShown && (
            <CloseButtonPopup
              handleClose={handleClose}
              paddings={[INDENTS.INDENT_40]}
              withPosterUrl={isWithPoster}
            />
          )}
        </PopupHeaderSC>
        {components?.[1] ? (
          withGradient ? (
            <PopupGradientOverlay
              isTopGradientShown={isTopGradientShown}
              isBottomGradientShown={isBottomGradientShown}
            >
              <PopupScrollableBlockWithGradient
                rowGapDescription={rowGapDescription}
                ref={popupBodyRef}
                onScroll={handleScroll}
              >
                {components?.[1]}
              </PopupScrollableBlockWithGradient>
            </PopupGradientOverlay>
          ) : (
            <PopupScrollableBlockDefault
              rowGapDescription={rowGapDescription}
              ref={popupBodyRef}
            >
              {components?.[1]}
            </PopupScrollableBlockDefault>
          )
        ) : (
          <></>
        )}
        {components?.[2] && (
          <PopupFooterWrapperSC>{components?.[2]}</PopupFooterWrapperSC>
        )}
        {isWithPoster && (
          <ImagePoster
            posterUrl={posterUrl}
            handleLoading={handleImageLoading}
          />
        )}
      </PopupWindow>
      {/*TODO: move ErrorMessage outside Popup and find the way to use ErrorMessage separately from Popup */}
      {errorMessage && <ErrorMessage title={errorMessage} />}
    </PopupOverlay>
  ) : (
    <></>
  )
}
